import React, { useState, useRef, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Upload, Camera, Video, X, Loader2, Image as ImageIcon, FileText, Smartphone } from 'lucide-react';

// Read file as base64 data URL, resize images to max 2048px to control payload size
const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const resizeImageDataURL = (dataUrl, maxDim = 2048, quality = 0.85) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

/**
 * Patient file upload — supports:
 *   • Desktop file picker
 *   • Mobile native camera capture (input capture="environment")
 *   • Desktop webcam fallback via getUserMedia
 */
export default function PatientFileUpload({ patientId, folders = [], onUploaded, trigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('choose'); // 'choose' | 'file' | 'webcam' | 'preview'
  const [preview, setPreview] = useState(null); // { dataUrl, filename, mime_type, source }
  const [folderId, setFolderId] = useState('');
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);

  // Webcam state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState('');

  const fileInputRef = useRef(null);
  const mobileCaptureRef = useRef(null);

  // Detect mobile (just a hint for UI — mobile browsers support <input capture>)
  const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

  const reset = () => {
    stopCamera();
    setMode('choose');
    setPreview(null);
    setNote('');
    setFolderId('');
    setCameraError('');
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const startCamera = async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setMode('webcam');
    } catch (err) {
      console.error(err);
      setCameraError(err?.message || 'Unable to access camera. Please check permissions.');
      setMode('choose');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const snapPhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    stopCamera();
    const filename = `webcam-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.jpg`;
    setPreview({ dataUrl, filename, mime_type: 'image/jpeg', source: 'webcam' });
    setMode('preview');
  };

  const handleFileSelect = async (file, source) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File is too large (max 15 MB)');
      return;
    }
    try {
      let dataUrl = await readFileAsDataURL(file);
      // Compress images (not PDFs) to keep payload reasonable
      if (file.type.startsWith('image/')) {
        dataUrl = await resizeImageDataURL(dataUrl, 2048, 0.85);
      }
      setPreview({
        dataUrl,
        filename: file.name || `upload-${Date.now()}`,
        mime_type: file.type || 'application/octet-stream',
        source: source || 'file',
      });
      setMode('preview');
    } catch (e) {
      toast.error('Failed to read file');
    }
  };

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      const r = await api.post(`/patients/${patientId}/files/upload`, {
        filename: preview.filename,
        mime_type: preview.mime_type,
        data_url: preview.dataUrl,
        folder_id: folderId || '',
        note,
        source: preview.source,
      });
      toast.success('File uploaded');
      onUploaded && onUploaded(r.data);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Cleanup camera if dialog is closed unexpectedly
  useEffect(() => () => stopCamera(), []);

  return (
    <>
      <span onClick={() => setOpen(true)}>
        {trigger || (
          <Button size="sm" className="careiq-btn-primary" data-testid="patient-upload-file-btn">
            <Upload className="w-3 h-3 mr-1" />Upload File
          </Button>
        )}
      </span>

      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
        <DialogContent className="max-w-xl" data-testid="patient-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Patient File</DialogTitle>
          </DialogHeader>

          {mode === 'choose' && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-[#64748B]">Choose how to add a file: pick from your device, snap a photo with your phone camera, or use your webcam.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#F0FDFA] transition-colors"
                  data-testid="upload-mode-file"
                >
                  <FileText className="w-6 h-6 text-[#0E7490]" />
                  <div className="text-sm font-medium text-[#0A2342]">Pick a File</div>
                  <div className="text-[10px] text-[#64748B] text-center">Images, PDFs, scans</div>
                </button>

                <button
                  onClick={() => mobileCaptureRef.current?.click()}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#F0FDFA] transition-colors"
                  data-testid="upload-mode-mobile-camera"
                >
                  <Smartphone className="w-6 h-6 text-[#0E7490]" />
                  <div className="text-sm font-medium text-[#0A2342]">Mobile Camera</div>
                  <div className="text-[10px] text-[#64748B] text-center">{isMobile ? 'Tap to snap a photo' : 'Best on phones/tablets'}</div>
                </button>

                <button
                  onClick={startCamera}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-dashed border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#F0FDFA] transition-colors"
                  data-testid="upload-mode-webcam"
                >
                  <Video className="w-6 h-6 text-[#0E7490]" />
                  <div className="text-sm font-medium text-[#0A2342]">Webcam</div>
                  <div className="text-[10px] text-[#64748B] text-center">Desktop camera</div>
                </button>
              </div>

              {cameraError && (
                <div className="p-2 text-xs rounded bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B]">{cameraError}</div>
              )}

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0], 'file')}
                data-testid="upload-file-input"
              />
              <input
                ref={mobileCaptureRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0], 'mobile_camera')}
                data-testid="upload-mobile-capture-input"
              />
            </div>
          )}

          {mode === 'webcam' && (
            <div className="space-y-3 py-2">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="webcam-video" />
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={() => { stopCamera(); setMode('choose'); }} data-testid="webcam-cancel-btn">
                  <X className="w-4 h-4 mr-1" />Cancel
                </Button>
                <Button className="careiq-btn-primary" onClick={snapPhoto} data-testid="webcam-capture-btn">
                  <Camera className="w-4 h-4 mr-1" />Capture Photo
                </Button>
              </div>
            </div>
          )}

          {mode === 'preview' && preview && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-[#E2E8F0] overflow-hidden bg-[#F8FAFC]">
                {preview.mime_type?.startsWith('image/') ? (
                  <img src={preview.dataUrl} alt="preview" className="w-full max-h-64 object-contain" data-testid="upload-preview-img" />
                ) : (
                  <div className="p-4 flex items-center gap-3 text-sm text-[#0A2342]">
                    <ImageIcon className="w-6 h-6 text-[#0E7490]" />
                    <div className="truncate">{preview.filename}</div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Filename</Label>
                <Input
                  value={preview.filename}
                  onChange={(e) => setPreview(p => ({ ...p, filename: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="upload-filename-input"
                />
              </div>

              {folders.length > 0 && (
                <div>
                  <Label className="text-xs">Folder (optional)</Label>
                  <Select value={folderId} onValueChange={setFolderId}>
                    <SelectTrigger className="h-8 text-sm" data-testid="upload-folder-select"><SelectValue placeholder="— Unfiled —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">— Unfiled —</SelectItem>
                      {folders.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g., Insurance card front"
                  className="min-h-[60px] text-sm"
                  data-testid="upload-note-input"
                />
              </div>

              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={reset} data-testid="upload-back-btn">
                  <X className="w-4 h-4 mr-1" />Back
                </Button>
                <Button className="careiq-btn-primary" onClick={handleUpload} disabled={uploading} data-testid="upload-save-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
